import { Box, Link } from "@mui/material";
import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useMemo, useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useTheme, useMediaQuery } from "@mui/material";
import FeaturedOnHeading from "../../../components/common/FeaturedOnHeading";

// Reuses the NewsLogoSection behavior, but scroll direction is left-to-right.
const scrollLeftToRight = keyframes`
  0% { transform: translateX(-50%); }
  100% { transform: translateX(0%); }
`;

const ScrollingTrack = styled(Box)`
  display: flex;
  align-items: center;
  gap: 40px;
  width: max-content;
  animation: ${scrollLeftToRight} 40s linear infinite;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const DEFAULT_AWARD_SECTION_ID = 117;

const AwardsSection = ({ cmsSectionId = DEFAULT_AWARD_SECTION_ID, padding = "30px 0" }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const home = useSelector((state) => state.cms.home);
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [needsScroll, setNeedsScroll] = useState(true);

  const { headingSubtitle, items } = useMemo(() => {
    const sections = Array.isArray(home?.data) ? home.data : [];
    const section = sections.find((s) => s?.id === cmsSectionId);
    const related = Array.isArray(section?.relatedCMS) ? section.relatedCMS : [];
    const headingSubtitle = (section?.title || "").trim();
    const items = related
      .filter((r) => r && (r.status === 1 || r.status === true))
      .map((r, idx) => ({
        id: r.id ?? idx,
        image: r.image || "",
        link: (r.link || "#").trim() || "#",
      }))
      .filter((r) => r.image);
    return { headingSubtitle, items };
  }, [home, cmsSectionId]);

  useEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure || items.length === 0) {
      setNeedsScroll(false);
      return;
    }

    const checkOverflow = () => {
      const contentWidth = measure.scrollWidth;
      const containerWidth = container.clientWidth;
      setNeedsScroll(contentWidth > containerWidth);
    };

    checkOverflow();
    const ro = new ResizeObserver(checkOverflow);
    ro.observe(container);
    return () => ro.disconnect();
  }, [items]);

  const Item = ({ item }) => (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <Link
        href={item.link || "#"}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none", cursor: item.link ? "pointer" : "default" }}
      >
        <Box
          component="img"
          src={item.image}
          alt=""
          sx={{
            width: "auto",
            height: "auto",
            objectFit: "contain",
            maxHeight: { xs: "56px", sm: "72px", md: "84px", lg: "96px" },
            maxWidth: { xs: "112px", sm: "144px", md: "168px", lg: "192px" },
          }}
        />
      </Link>
    </Box>
  );

  if (items.length === 0) {
    return null;
  }

  return (
    <Box sx={{ width: "94.44%", maxWidth: "1440px", margin: "0 auto", mb: isMobile ? "0px" : "30px" }}>
      <FeaturedOnHeading title="Awards" subtitle={headingSubtitle || undefined} />
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          overflow: "hidden",
          padding,
          position: "relative",
          ...(needsScroll && {
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: 0,
              width: "50px",
              height: "100%",
              zIndex: 2,
              pointerEvents: "none",
              background: "linear-gradient(to right, rgba(255,255,255,1), rgba(255,255,255,0))",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              right: 0,
              top: 0,
              width: "50px",
              height: "100%",
              zIndex: 2,
              pointerEvents: "none",
              background: "linear-gradient(to left, rgba(255,255,255,1), rgba(255,255,255,0))",
            },
          }),
        }}
      >
        <Box
          ref={measureRef}
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            visibility: "hidden",
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            gap: "40px",
            width: "max-content",
          }}
        >
          {items.map((item, index) => (
            <Item key={`measure-${item.id}-${index}`} item={item} />
          ))}
        </Box>

        {needsScroll ? (
          <ScrollingTrack>
            {items.map((item, index) => (
              <Item key={`first-${item.id}-${index}`} item={item} />
            ))}
            {items.map((item, index) => (
              <Item key={`second-${item.id}-${index}`} item={item} />
            ))}
          </ScrollingTrack>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "40px", flexWrap: "wrap" }}>
            {items.map((item, index) => (
              <Item key={`row-${item.id}-${index}`} item={item} />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AwardsSection;

